/**
 * ChampNombre — un nombre, avec − et + au bout : un seuil (« 100 likes au
 * moins »), une durée, un nombre de comptes. Bornes, pas, unité.
 *
 *   <ChampNombre etiquette="Likes au moins" valeur={min} onChange={setMin} min={0} pas={50} />
 *   <ChampNombre etiquette="Délai" valeur={delai} onChange={setDelai} min={1} max={60} unite="min" />
 *
 * Vide, c'est `null` (« aucun seuil ») — jamais un 0 inventé. ↑ ↓ ajoutent ou
 * retirent un pas (Maj : dix pas) ; le nombre est remis dans ses bornes en
 * sortant du champ, pas pendant qu'on tape (« 1 » en route vers « 12 » n'est
 * pas une erreur). `role="spinbutton"` : un lecteur d'écran l'annonce comme tel.
 *
 * Successeur des 27 `<input type="number">` du cockpit (seuils des filtres de
 * captions, achats de comptes et de proxys, réglages du node-studio…), dont le
 * rendu natif (les petites flèches grises) sortait du thème.
 */
import { useEffect, useRef, useState, type ReactNode, type Ref } from "react";

import { Button } from "../Button";
import { Champ, type TailleChamp } from "../Champ";
import { Minus, Plus } from "../icons";
import { cn } from "../utils";
import "./saisie.css";

export interface ChampNombreProps {
  valeur: number | null;
  onChange: (valeur: number | null) => void;
  min?: number;
  max?: number;
  /** Le pas des boutons et des flèches (défaut 1). Un pas décimal (0,5) autorise les décimales. */
  pas?: number;
  /** À droite, en gris : « min », « % », « comptes ». */
  unite?: ReactNode;
  etiquette?: ReactNode;
  aide?: ReactNode;
  erreur?: ReactNode;
  placeholder?: string;
  /** Sans les boutons − et + (le champ seul). */
  sansBoutons?: boolean;
  /** Les étiquettes des boutons — déjà traduites (défaut « Moins », « Plus »). */
  moinsLabel?: string;
  plusLabel?: string;
  /** Le format des décimales (la virgule en fr). Défaut fr-FR. */
  locale?: string;
  taille?: TailleChamp;
  disabled?: boolean;
  id?: string;
  name?: string;
  "aria-label"?: string;
  ref?: Ref<HTMLInputElement>;
  className?: string;
}

function decimalesDe(pas: number): number {
  const s = String(pas);
  const i = s.indexOf(".");
  return i < 0 ? 0 : s.length - i - 1;
}

function formaterNombre(n: number | null, locale: string, decimales: number): string {
  return n === null ? "" : new Intl.NumberFormat(locale, { maximumFractionDigits: Math.max(decimales, 2), useGrouping: false }).format(n);
}

/** « 12,5 » ou « 12.5 » → 12.5 ; vide → null ; autre chose → undefined. */
export function nombreDepuisTexte(texte: string): number | null | undefined {
  const s = texte.replace(/[\s  ]/g, "").replace(",", ".");
  if (s === "") return null;
  if (!/^-?\d*\.?\d*$/.test(s) || s === "-" || s === "." || s === "-.") return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

export function ChampNombre({
  valeur,
  onChange,
  min,
  max,
  pas = 1,
  unite,
  etiquette,
  aide,
  erreur,
  placeholder,
  sansBoutons = false,
  moinsLabel = "Moins",
  plusLabel = "Plus",
  locale = "fr-FR",
  taille = "md",
  disabled,
  id,
  name,
  "aria-label": ariaLabel,
  ref,
  className,
}: ChampNombreProps) {
  const decimales = decimalesDe(pas);
  const ecrire = (n: number | null) => formaterNombre(n, locale, decimales);
  const [texte, setTexte] = useState(() => ecrire(valeur));
  const dedans = useRef(false);

  useEffect(() => {
    if (dedans.current) return;
    setTexte(formaterNombre(valeur, locale, decimales));
  }, [valeur, locale, decimales]);

  const borner = (n: number) => {
    let x = n;
    if (min !== undefined) x = Math.max(min, x);
    if (max !== undefined) x = Math.min(max, x);
    return Number(x.toFixed(decimales));
  };
  const pousser = (sens: 1 | -1, fois = 1) => {
    const depart = valeur ?? (sens > 0 ? (min ?? 0) - pas : (max ?? 0) + pas);
    const n = borner(depart + sens * pas * fois);
    onChange(n);
    setTexte(ecrire(n));
  };

  const bouton = (sens: 1 | -1) => (
    <Button
      variant="ghost"
      size="sm"
      iconOnly
      tabIndex={-1}
      className="si-dans-le-champ"
      aria-label={sens > 0 ? plusLabel : moinsLabel}
      title={sens > 0 ? plusLabel : moinsLabel}
      disabled={disabled || (sens > 0 ? max !== undefined && valeur !== null && valeur >= max : min !== undefined && valeur !== null && valeur <= min)}
      onPointerDown={(e) => e.preventDefault()}
      onClick={() => pousser(sens)}
    >
      {sens > 0 ? <Plus weight="bold" aria-hidden /> : <Minus weight="bold" aria-hidden />}
    </Button>
  );

  const suffixe =
    unite !== undefined || !sansBoutons ? (
      <span className="flex items-center gap-1">
        {unite !== undefined && <span className="mr-1 text-faint">{unite}</span>}
        {!sansBoutons && (
          <>
            {bouton(-1)}
            {bouton(1)}
          </>
        )}
      </span>
    ) : undefined;

  return (
    <Champ
      ref={ref as Ref<HTMLInputElement | HTMLTextAreaElement>}
      id={id}
      name={name}
      type="text"
      inputMode={decimales > 0 || (min !== undefined && min < 0) ? "decimal" : "numeric"}
      role="spinbutton"
      aria-valuenow={valeur ?? undefined}
      aria-valuemin={min}
      aria-valuemax={max}
      aria-label={etiquette === undefined ? ariaLabel : undefined}
      autoComplete="off"
      etiquette={etiquette}
      aide={aide}
      erreur={erreur}
      placeholder={placeholder}
      disabled={disabled}
      taille={taille}
      suffixe={suffixe}
      value={texte}
      className={cn("si-nombre", className)}
      onFocus={() => {
        dedans.current = true;
      }}
      onChange={(e) => {
        const v = e.target.value;
        const n = nombreDepuisTexte(v);
        if (n === undefined) return; // une lettre ne s'écrit pas
        setTexte(v);
        if (n !== valeur) onChange(n);
      }}
      onBlur={() => {
        dedans.current = false;
        const n = nombreDepuisTexte(texte);
        const propre = n === null || n === undefined ? null : borner(n);
        if (propre !== valeur) onChange(propre);
        setTexte(ecrire(propre));
      }}
      onKeyDown={(e) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault();
          pousser(e.key === "ArrowUp" ? 1 : -1, e.shiftKey ? 10 : 1);
        } else if (e.key === "Home" && min !== undefined) {
          e.preventDefault();
          onChange(min);
          setTexte(ecrire(min));
        } else if (e.key === "End" && max !== undefined) {
          e.preventDefault();
          onChange(max);
          setTexte(ecrire(max));
        }
      }}
    />
  );
}
